import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { ClipboardCheck, Plus, Trash2, Pencil, Loader2, Save, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api, { pdvApi } from '../lib/api';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';
import { useAuth } from '../contexts/AuthContext';
import HaccpConsultantHeader from '../components/HaccpConsultantHeader';

const RESPONSE_COLORS = {
  SI: 'bg-emerald-500 text-white border-emerald-500',
  NO: 'bg-red-500 text-white border-red-500',
  NA: 'bg-gray-300 text-gray-700 border-gray-300',
};

const emptyForm = () => ({
  pdv_id: '',
  titolo: '',
  responsabile_presente: '',
  data_ispezione: new Date().toISOString().slice(0, 10),
  ora_inizio: '10:00',
  ora_fine: '',
  answers: {},  // chiave: `${section_id}_${index}` -> {response, note}
  note_finali: '',
});

export default function HaccpInspectionPage() {
  const { currentPdvId } = useCurrentPdv();
  const { user } = useAuth();
  // Le aziende vedono solo i report (sola lettura)
  const isReadOnly = user?.role === 'azienda';
  const [sections, setSections] = useState([]);
  const [pdvList, setPdvList] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('doc');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, r] = await Promise.all([api.get('/haccp-inspection/template'), pdvApi.getAll(), api.get('/haccp-inspection')]);
      setSections(s.data); setPdvList(p.data); setRecords(r.data);
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    const f = emptyForm();
    if (currentPdvId && pdvList.some(p => p.id === currentPdvId)) {
      f.pdv_id = currentPdvId;
    } else if (pdvList.length === 1) {
      f.pdv_id = pdvList[0].id;
    }
    setForm(f); setActiveSection(sections[0]?.id || 'doc');
    setDialog(true);
  };
  const openEdit = async (rec) => {
    try {
      const full = await api.get(`/haccp-inspection/${rec.id}`);
      const d = full.data;
      const ansMap = {};
      (d.answers || []).forEach((a) => { ansMap[`${a.section_id}_${a.index}`] = { response: a.response, note: a.note || '' }; });
      setForm({
        pdv_id: d.pdv_id || '', titolo: d.titolo || '',
        responsabile_presente: d.responsabile_presente || '',
        data_ispezione: d.data_ispezione || '',
        ora_inizio: d.ora_inizio || '', ora_fine: d.ora_fine || '',
        answers: ansMap,
        note_finali: d.note_finali || '',
      });
      setEditingId(rec.id); setActiveSection(sections[0]?.id || 'doc');
      setDialog(true);
    } catch (e) { toast.error('Errore'); }
  };
  const handleSave = async () => {
    if (!form.pdv_id) { toast.error('Seleziona UL'); return; }
    setSaving(true);
    try {
      const answers = Object.entries(form.answers).map(([k, v]) => {
        const [section_id, idx] = k.split('_');
        return { section_id, index: parseInt(idx, 10), response: v.response || 'NA', note: v.note || '' };
      }).filter(a => a.response && (a.response !== 'NA' || a.note));
      const payload = { ...form, answers };
      delete payload.answers;
      payload.answers = answers;
      if (editingId) await api.put(`/haccp-inspection/${editingId}`, payload);
      else await api.post('/haccp-inspection', payload);
      toast.success(editingId ? 'Aggiornato' : 'Creato');
      setDialog(false); load();
    } catch (e) { toast.error('Errore'); }
    finally { setSaving(false); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare?')) return;
    try { await api.delete(`/haccp-inspection/${id}`); toast.success('Eliminato'); load(); }
    catch (e) { toast.error('Errore'); }
  };
  const handlePDF = (id) => {
    const token = sessionStorage.getItem('token');
    window.open(`/api/haccp-inspection/${id}/pdf?token=${token}`, '_blank');
  };

  const setAnswer = (sec, idx, patch) => {
    setForm(p => ({ ...p, answers: { ...p.answers, [`${sec}_${idx}`]: { ...(p.answers[`${sec}_${idx}`] || { response: 'NA', note: '' }), ...patch } } }));
  };
  const setAllInSection = (secId, response) => {
    const sec = sections.find(s => s.id === secId);
    if (!sec) return;
    const next = { ...form.answers };
    sec.items.forEach((_, i) => {
      next[`${secId}_${i}`] = { ...(next[`${secId}_${i}`] || { note: '' }), response };
    });
    setForm({ ...form, answers: next });
  };

  const pdvName = (id) => pdvList.find(p => p.id === id)?.name || '—';
  const selectedPdv = pdvList.find(p => p.id === form.pdv_id);
  const countSI = Object.values(form.answers).filter(a => a.response === 'SI').length;
  const countNO = Object.values(form.answers).filter(a => a.response === 'NO').length;
  const totalItems = sections.reduce((a, s) => a + s.items.length, 0);

  return (
    <div className="p-6 space-y-6" data-testid="haccp-inspection-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><ClipboardCheck className="w-7 h-7 text-emerald-500" /> HACCP — Check-list Ispezione</h1>
          <p className="text-gray-600 mt-1">Check-list completa di 70 punti su 5 sezioni: Controllo Documentale, Spogliatoio, WC, Controllo Infestanti, Autocontrollo.</p>
        </div>
        <Button onClick={openCreate} data-testid="new-inspection-btn" style={{display: isReadOnly ? 'none' : undefined}}><Plus className="w-4 h-4 mr-1" /> Nuova Ispezione</Button>
      </div>

      {loading ? <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        : records.length === 0 ? <Card><CardContent className="py-10 text-center text-gray-500">Nessuna ispezione.</CardContent></Card>
        : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {records.map((r) => {
              const ans = r.answers || [];
              const si = ans.filter(a => a.response === 'SI').length;
              const no = ans.filter(a => a.response === 'NO').length;
              return (
                <Card key={r.id}>
                  <CardHeader><CardTitle className="text-base">{r.titolo || `Ispezione ${pdvName(r.pdv_id)}`}</CardTitle></CardHeader>
                  <CardContent>
                    <Badge variant="outline" className="mb-2">{pdvName(r.pdv_id)}</Badge>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>📅 {r.data_ispezione || '—'}</div>
                      <div><span className="text-emerald-600 font-semibold">{si}</span> SI · <span className="text-red-600 font-semibold">{no}</span> NO</div>
                    </div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => handlePDF(r.id)} data-testid={`pdf-${r.id}`}><Download className="w-4 h-4 mr-1" /> PDF</Button>
                      {!isReadOnly && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => openEdit(r)}><Pencil className="w-4 h-4" /></Button>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(r.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto" data-testid="inspection-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3">
              <span>{editingId ? 'Modifica Ispezione' : 'Nuova Ispezione HACCP'}</span>
              <div className="text-sm font-normal text-gray-500">
                <span className="text-emerald-600 font-semibold">{countSI}</span> SI · <span className="text-red-600 font-semibold">{countNO}</span> NO · {totalItems} totali
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <HaccpConsultantHeader pdv={selectedPdv} dateLabel={form.data_ispezione} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label>UL *</Label>
                <Select value={form.pdv_id} onValueChange={(v) => setForm({ ...form, pdv_id: v })}>
                  <SelectTrigger data-testid="ul-select"><SelectValue placeholder="Seleziona UL" /></SelectTrigger>
                  <SelectContent>{pdvList.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Titolo</Label><Input value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} /></div>
              <div><Label>Responsabile presente</Label><Input value={form.responsabile_presente} onChange={(e) => setForm({ ...form, responsabile_presente: e.target.value })} /></div>
              <div><Label>Data</Label><Input type="date" value={form.data_ispezione} onChange={(e) => setForm({ ...form, data_ispezione: e.target.value })} /></div>
              <div><Label>Ora inizio</Label><Input type="time" value={form.ora_inizio} onChange={(e) => setForm({ ...form, ora_inizio: e.target.value })} /></div>
              <div><Label>Ora fine</Label><Input type="time" value={form.ora_fine} onChange={(e) => setForm({ ...form, ora_fine: e.target.value })} /></div>
            </div>

            <Tabs value={activeSection} onValueChange={setActiveSection}>
              <TabsList className="flex flex-wrap h-auto">
                {sections.map((s) => {
                  const ans = s.items.map((_, i) => form.answers[`${s.id}_${i}`]?.response).filter(Boolean);
                  const si = ans.filter(x => x === 'SI').length;
                  return (
                    <TabsTrigger key={s.id} value={s.id} className="text-xs">
                      {s.label} <span className="ml-1 text-gray-400">({si}/{s.items.length})</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              {sections.map((s) => (
                <TabsContent key={s.id} value={s.id} className="space-y-2 mt-3">
                  <div className="flex justify-end gap-2 pb-2 text-xs">
                    <span className="text-gray-500 self-center mr-2">Imposta tutti:</span>
                    <button type="button" onClick={() => setAllInSection(s.id, 'SI')} className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200">Tutti SI</button>
                    <button type="button" onClick={() => setAllInSection(s.id, 'NA')} className="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200">Tutti N.A.</button>
                  </div>
                  {s.items.map((item, i) => {
                    const k = `${s.id}_${i}`;
                    const a = form.answers[k] || { response: 'NA', note: '' };
                    return (
                      <div key={i} className="grid grid-cols-12 gap-2 items-start border-b py-2">
                        <div className="col-span-1 text-xs text-gray-400 pt-2">{i + 1}.</div>
                        <div className="col-span-6 text-sm pt-2">{item}</div>
                        <div className="col-span-2 flex gap-1">
                          {['SI', 'NO', 'NA'].map((r) => (
                            <button key={r} type="button" onClick={() => setAnswer(s.id, i, { response: r })}
                              className={`flex-1 px-2 py-1 text-xs font-semibold rounded border transition-colors ${a.response === r ? RESPONSE_COLORS[r] : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'}`}
                              data-testid={`ans-${s.id}-${i}-${r}`}>
                              {r === 'NA' ? 'N.A.' : r}
                            </button>
                          ))}
                        </div>
                        <div className="col-span-3">
                          <Input value={a.note || ''} onChange={(e) => setAnswer(s.id, i, { note: e.target.value })} placeholder="Note" className="text-xs" />
                        </div>
                      </div>
                    );
                  })}
                </TabsContent>
              ))}
            </Tabs>

            <div><Label>Note finali</Label><Textarea rows={2} value={form.note_finali} onChange={(e) => setForm({ ...form, note_finali: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Salva Ispezione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
